import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Zap, Sun, Heart, BookOpen, Columns } from "lucide-react";
import { useLang } from "@/lib/lang";

export default function AmisDesCeltes() {
  const { lang, setLang } = useLang();

  const cards = [
    {
      id: "zeus",
      href: "/zeus",
      icon: Zap,
      accent: "#ffd84a",
      title: { fr: "Zeus", en: "Zeus" },
      subtitle: { fr: "Roi de l'Olympe", en: "King of Olympus" },
      body: {
        fr: "Autoritaire, foudroyant, bienveillant. Bouscule la tiédeur par ses questions — sans rien ordonner, il te laisse choisir seul.",
        en: "Authoritarian, thunderous, benevolent. Shakes lukewarmness with questions — orders nothing, leaves you to choose alone.",
      },
      meta: { fr: "gratuit · illimité", en: "free · unlimited" },
    },
    {
      id: "apollon",
      href: "/apollon",
      icon: Sun,
      accent: "#ff9a4a",
      title: { fr: "Apollon", en: "Apollo" },
      subtitle: { fr: "L'ami lumineux", en: "The luminous friend" },
      body: {
        fr: "Dieu de la lumière, de la musique et des Muses. Ami doux et sage qui connaît tout mais t'offre toujours plusieurs options — jamais une seule réponse.",
        en: "God of light, music, and the Muses. A gentle wise friend who knows all but always offers you several options — never one single answer.",
      },
      meta: { fr: "gratuit · illimité", en: "free · unlimited" },
    },
    {
      id: "eros",
      href: "/eros",
      icon: Heart,
      accent: "#ff6b9d",
      title: { fr: "Éros", en: "Eros" },
      subtitle: { fr: "Éducateur à l'amour", en: "Teacher of love" },
      body: {
        fr: "Dieu primordial de l'Amour. Enseigne les 4 formes grecques — Éros, Philia, Storgé, Agapè. Sage doux qui illumine sans écraser.",
        en: "Primordial god of Love. Teaches the 4 Greek forms — Eros, Philia, Storge, Agape. A gentle sage who illuminates without crushing.",
      },
      meta: { fr: "gratuit · illimité", en: "free · unlimited" },
    },
    {
      id: "urantia",
      href: "/urantia",
      icon: BookOpen,
      accent: "#64a0ff",
      title: { fr: "Livre d'Urantia", en: "Urantia Book" },
      subtitle: { fr: "L'Ajusteur de Pensée", en: "The Thought Adjuster" },
      body: {
        fr: "Recueil cosmologique de 1955. Enseigne via l'Ajusteur de Pensée — montre un chemin, puis fait DOUTER sainement. Professeur direct et pragmatique.",
        en: "1955 cosmological book. Teaches via the Thought Adjuster — shows a path, then plants healthy DOUBT. Direct, pragmatic teacher.",
      },
      meta: { fr: "gratuit · illimité", en: "free · unlimited" },
    },
  ];

  return (
    <div data-testid="amis-page" className="min-h-screen relative"
         style={{
           background: "radial-gradient(ellipse at top, #0f1a2a 0%, #080e1a 55%, #04070e 100%)",
           color: "#e8f0ff",
           overflowX: "hidden",
         }}>
      <header className="relative z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#64a0ff" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
               style={{ background: "rgba(50,80,120,0.3)", border: "1px solid rgba(100,160,255,0.3)" }}>
            <button onClick={() => setLang("fr")} type="button" data-testid="amis-lang-fr" className="px-2.5 py-1.5"
                    style={{ background: lang === "fr" ? "#64a0ff" : "transparent", color: lang === "fr" ? "#040812" : "#e8f0ff", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
            <button onClick={() => setLang("en")} type="button" data-testid="amis-lang-en" className="px-2.5 py-1.5"
                    style={{ background: lang === "en" ? "#64a0ff" : "transparent", color: lang === "en" ? "#040812" : "#e8f0ff", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-10">
        <div className="overline mb-4" style={{ color: "#64a0ff" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Formation · Amis des Celtes" : "Formation · Celts' Friends"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f8faff" }}>
          {lang === "fr" ? (
            <>Les <span className="italic" style={{ color: "#64a0ff" }}>Grecs</span> & le <span className="italic" style={{ color: "#64a0ff" }}>Livre d'Urantia</span>,</>
          ) : (
            <>The <span className="italic" style={{ color: "#64a0ff" }}>Greeks</span> & the <span className="italic" style={{ color: "#64a0ff" }}>Urantia Book</span>,</>
          )}
          <br />
          <span style={{ color: "#a8c0e8" }}>
            {lang === "fr" ? "frères spirituels des Celtes." : "spiritual brothers of the Celts."}
          </span>
        </h1>

        <div className="mt-10 grid gap-6 lg:grid-cols-[2fr,3fr] items-start">
          <div className="p-6 rounded-2xl"
               style={{ background: "linear-gradient(180deg, rgba(20,36,60,0.9), rgba(8,16,30,0.95))",
                        border: "1px solid rgba(100,160,255,0.3)" }}>
            <Columns size={20} style={{ color: "#64a0ff" }} />
            <h2 className="font-serif text-xl mt-3 mb-2" style={{ color: "#f8faff" }}>
              {lang === "fr" ? "Deux mondes, une même racine" : "Two worlds, one root"}
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: "#c9d8f5" }}>
              {lang === "fr"
                ? "Les Grecs et les Celtes irlandais sont deux branches du grand arbre indo-européen. Dès le VIe siècle av. J.-C., des marchands grecs commerçaient avec les Gaulois et les Celtibères (fondation de Massalia/Marseille en 600 av. J.-C.). Le philosophe Posidonius d'Apamée vécut parmi les druides et nota leur sagesse. Les deux peuples honorent le chêne, la pomme (Avalon ≈ Hespérides), les voyages maritimes et l'immortalité de l'âme."
                : "The Greeks and Irish Celts are two branches of the great Indo-European tree. As early as the 6th century BCE, Greek traders dealt with the Gauls and Celtiberians (founding of Massalia/Marseille in 600 BCE). The philosopher Posidonius of Apamea lived among the Druids and recorded their wisdom. Both peoples honor the oak, the apple (Avalon ≈ Hesperides), sea voyages, and the immortality of the soul."}
            </p>
          </div>
          <div className="p-6 rounded-2xl"
               style={{ background: "linear-gradient(180deg, rgba(20,36,60,0.9), rgba(8,16,30,0.95))",
                        border: "1px solid rgba(100,160,255,0.3)" }}>
            <BookOpen size={20} style={{ color: "#64a0ff" }} />
            <h2 className="font-serif text-xl mt-3 mb-2" style={{ color: "#f8faff" }}>
              {lang === "fr" ? "Trois leçons des Grecs & d'Urantia" : "Three lessons from the Greeks & Urantia"}
            </h2>
            <ul className="text-sm leading-relaxed space-y-2.5" style={{ color: "#c9d8f5" }}>
              <li><strong style={{ color: "#ffd84a" }}>⚡ Zeus</strong> — {lang === "fr" ? "connaissance de la force juste : la foudre réveille les tièdes, elle ne détruit pas les faibles." : "knowledge of righteous force: thunder wakes the lukewarm, it does not destroy the weak."}</li>
              <li><strong style={{ color: "#ff9a4a" }}>☀️ Apollon</strong> — {lang === "fr" ? "la vérité se trouve en soi (gnōthi seauton, Delphes), et un ami éclaire plusieurs voies plutôt que d'en imposer une." : "truth is found within (gnōthi seauton, Delphi), and a friend lights several ways rather than imposing one."}</li>
              <li><strong style={{ color: "#ff6b9d" }}>🏹 Éros</strong> — {lang === "fr" ? "l'amour est pluriel (Eros, Philia, Storgé, Agapè). Les Celtes aussi distinguaient l'amour-désir du grá (amour-tendresse)." : "love is plural (Eros, Philia, Storge, Agape). The Celts also distinguished desire-love from grá (tender-love)."}</li>
              <li><strong style={{ color: "#64a0ff" }}>📖 Urantia</strong> — {lang === "fr" ? "l'Ajusteur de Pensée (fragment du Père Universel) rejoint le gníomhach interne du druidisme : la part divine en soi qui sait avant que l'intellect ne décide." : "the Thought Adjuster (fragment of the Universal Father) echoes the internal gníomhach of Druidism: the divine part within that knows before the intellect decides."}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        {/* Olympian Debate banner */}
        <Link
          to="/debat-olympien"
          data-testid="amis-debate-banner"
          className="block mb-8 p-5 rounded-2xl transition-all hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(135deg, rgba(255,216,74,0.15), rgba(255,154,74,0.15))",
            border: "1.5px solid rgba(255,216,74,0.5)",
            boxShadow: "0 20px 50px -25px rgba(255,154,74,0.5)",
          }}
        >
          <div className="flex items-center gap-4">
            <div className="text-3xl">⚔️</div>
            <div className="flex-1">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#ffd84a" }}>
                {lang === "fr" ? "Nouveau · Mode débat" : "New · Debate mode"}
              </div>
              <h3 className="font-serif text-xl sm:text-2xl" style={{ color: "#fff2c7" }}>
                {lang === "fr" ? "Zeus & Apollon répondent en parallèle" : "Zeus & Apollon answer in parallel"}
              </h3>
              <p className="text-sm mt-1" style={{ color: "#c9d8f5" }}>
                {lang === "fr"
                  ? "Une seule question, deux voix légitimes. L'anti-oracle par excellence : le libre-arbitre devient ton seul maître."
                  : "One question, two legitimate voices. The anti-oracle par excellence: free will becomes your only master."}
              </p>
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] hidden sm:block" style={{ color: "#ffd84a" }}>
              {lang === "fr" ? "Entrer →" : "Enter →"}
            </div>
          </div>
        </Link>

        <h2 className="font-serif text-2xl sm:text-3xl mb-6" style={{ color: "#f8faff" }}>
          {lang === "fr" ? "Choisis ton maître·sse d'étude" : "Pick your teacher"}
        </h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.id}
                to={c.href}
                data-testid={`amis-card-${c.id}`}
                className="group p-5 rounded-2xl relative overflow-hidden transition-all hover:-translate-y-1"
                style={{
                  background: `linear-gradient(180deg, ${c.accent}15, rgba(8,14,26,0.95))`,
                  border: `1px solid ${c.accent}50`,
                  boxShadow: `0 20px 50px -30px ${c.accent}80`,
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <Icon size={24} style={{ color: c.accent }} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: `${c.accent}bb` }}>
                    {c.meta[lang]}
                  </span>
                </div>
                <h3 className="font-serif text-2xl" style={{ color: c.accent }}>
                  {c.title[lang]}
                </h3>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] mt-0.5 mb-3" style={{ color: `${c.accent}aa` }}>
                  {c.subtitle[lang]}
                </p>
                <p className="text-sm leading-relaxed" style={{ color: "#c9d8f5" }}>
                  {c.body[lang]}
                </p>
                <div className="mt-4 text-xs font-mono uppercase tracking-[0.2em] opacity-80 group-hover:opacity-100 transition-opacity"
                     style={{ color: c.accent }}>
                  {lang === "fr" ? "Entrer →" : "Enter →"}
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
